import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Sparkles, FileDown, ExternalLink, AlertTriangle } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasPerm } from "../../lib/permissions";
import { PageHead, Empty, ago, pdfUrl } from "./common";

export default function ShippingNews() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [f, setF] = useState({ category: "", country: "", q: "", disruptions_only: false, page: 1 });
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState("");
  const canEdit = hasPerm(user, "sea_freight", "create");
  const canAnalyze = hasPerm(user, "sea_freight", "create");

  const load = useCallback(async (override) => {
    const q = { ...f, ...(override || {}) };
    try {
      const { data: d } = await api.get("/sea-freight/news", { params: { ...q, page_size: 20 } });
      setData(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [f]);

  useEffect(() => { load({}); /* eslint-disable-next-line */ }, [f.category, f.disruptions_only, f.page]);

  const sync = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/sea-freight/news/sync");
      toast.success(`${r.added} new article(s) from ${r.sources} feed(s)`);
      load({ page: 1 });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const analyze = async (n) => {
    setAiBusy(n.id);
    try {
      const { data: a } = await api.post(`/sea-freight/news/${n.id}/analyze`);
      setAi({ article: n, analysis: a });
      load({});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setAiBusy("");
  };

  const pages = data ? Math.ceil(data.total / data.page_size) : 1;

  return (
    <div data-testid="shipping-news-page">
      <PageHead title="Global Shipping News"
        subtitle="Collected automatically from maritime news feeds every 30 minutes. Headlines and links are the publisher's; AI analysis is clearly marked as prediction."
        right={<>
          <Button data-testid="export-news-pdf" variant="outline" className="rounded-sm gap-1.5"
            onClick={() => window.open(pdfUrl("/sea-freight/export/news.pdf", { category: f.category, q: f.q }), "_blank")}>
            <FileDown className="w-4 h-4" /> PDF
          </Button>
          {canEdit && <Button data-testid="sync-news-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" disabled={busy} onClick={sync}>
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Fetch latest
          </Button>}
        </>} />

      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <Select value={f.category || "all"} onValueChange={(v) => setF({ ...f, category: v === "all" ? "" : v, page: 1 })}>
          <SelectTrigger data-testid="news-category" className="bg-white h-9"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(data?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="news-country" placeholder="Country / port / route" value={f.country}
          onChange={(e) => setF({ ...f, country: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load({ page: 1 })} className="bg-white h-9" />
        <Input data-testid="news-search" placeholder="Search keywords" value={f.q}
          onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load({ page: 1 })} className="bg-white h-9" />
        <Select value={f.disruptions_only ? "dis" : "all"} onValueChange={(v) => setF({ ...f, disruptions_only: v === "dis", page: 1 })}>
          <SelectTrigger data-testid="news-disruptions" className="bg-white h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All news</SelectItem><SelectItem value="dis">Disruptions only</SelectItem></SelectContent>
        </Select>
      </div>

      {data && <div className="text-xs text-slate-500 mb-2" data-testid="news-meta">{data.total} article(s) · last collection {ago(data.last_sync_at)}</div>}

      <div className="space-y-2">
        {data && data.news.length === 0 && <Empty testid="news-empty">No articles yet. Click "Fetch latest" to collect from the configured feeds.</Empty>}
        {(data?.news || []).map((n) => (
          <article key={n.id} data-testid={`news-${n.id}`} className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <a href={n.url} target="_blank" rel="noreferrer" className="text-sm sm:text-base font-semibold text-slate-900 hover:text-[#1C2541] inline-flex items-start gap-1.5">
                  {n.headline} <ExternalLink className="w-3.5 h-3.5 mt-1 shrink-0 text-slate-400" />
                </a>
                {n.summary && <p className="text-sm text-slate-600 mt-1 line-clamp-3">{n.summary}</p>}
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <Badge variant="outline" className="rounded-full text-[11px] px-2">{n.source_name}</Badge>
                  {n.is_disruption && <Badge variant="outline" className="rounded-full text-[11px] px-2 bg-amber-100 text-amber-800 border-amber-200"><AlertTriangle className="w-3 h-3 mr-1" /> Disruption</Badge>}
                  {(n.categories || []).map((c) => <Badge key={c} variant="outline" className="rounded-full text-[11px] px-2 bg-slate-50">{c}</Badge>)}
                  <span className="text-[11px] text-slate-400">published {ago(n.published_at)} · collected {ago(n.collected_at)}</span>
                </div>
              </div>
              {(canAnalyze || n.ai_analysis) && (
                <Button data-testid={`analyze-${n.id}`} size="sm" variant="outline" className="rounded-sm gap-1.5 shrink-0"
                  disabled={aiBusy === n.id || (!canAnalyze && !n.ai_analysis)} onClick={() => (n.ai_analysis ? setAi({ article: n, analysis: n.ai_analysis }) : analyze(n))}>
                  <Sparkles className="w-3.5 h-3.5" /> {n.ai_analysis ? "View AI analysis" : aiBusy === n.id ? "Analysing..." : "AI analysis"}
                </Button>
              )}
            </div>
          </article>
        ))}
      </div>

      {data && pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4" data-testid="news-pagination">
          <Button variant="outline" size="sm" className="rounded-sm" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })}>Previous</Button>
          <span className="text-xs text-slate-500">Page {f.page} of {pages}</span>
          <Button variant="outline" size="sm" className="rounded-sm" disabled={f.page >= pages} onClick={() => setF({ ...f, page: f.page + 1 })}>Next</Button>
        </div>
      )}

      <Dialog open={!!ai} onOpenChange={() => setAi(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="ai-analysis-dialog">
          <DialogHeader>
            <DialogTitle className="font-head text-base">{ai?.article?.headline}</DialogTitle>
            <DialogDescription>
              Verified fact: the headline and link above, published by {ai?.article?.source_name}. Everything below is AI-generated analysis.
            </DialogDescription>
          </DialogHeader>
          {ai && (
            <div className="space-y-3 text-sm">
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-sm p-2 text-xs">{ai.analysis.disclaimer || "AI-generated analysis — not a confirmed market event."}</div>
              {[["what_happened", "What happened"], ["affected_countries", "Countries affected"], ["affected_ports", "Ports affected"],
                ["affected_routes", "Routes affected"], ["freight_rate_impact", "Potential freight rate impact"],
                ["vessel_delay_impact", "Potential vessel delay"], ["import_export_impact", "Import / export impact"],
                ["aerobent_relevance", "Relevance to Aerobent Minechem"], ["recommended_action", "Recommended business action"]].map(([k, label]) => (
                ai.analysis[k] ? (
                  <div key={k}>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
                    <div className="text-slate-800">{Array.isArray(ai.analysis[k]) ? ai.analysis[k].join(", ") : String(ai.analysis[k])}</div>
                  </div>
                ) : null
              ))}
              <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
                Model {ai.analysis.model} · confidence {ai.analysis.confidence || "n/a"} · generated {ago(ai.analysis.generated_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
